import React, { useState, useEffect, useCallback } from 'react';
import { signOut } from 'firebase/auth';
import { auth, db, storage } from '../firebase';
import { collection, query, where, getDocs, addDoc, deleteDoc, doc, setDoc, updateDoc, orderBy, getDoc, writeBatch } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { 
  Calendar, LogOut, Clock, Phone, User, Users, Plus, Trash2, X, Settings, 
  DollarSign, MapPin, AlertCircle, Check, Loader, Globe, ChevronLeft, ChevronRight, CalendarDays, UploadCloud
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import RecurringBookingModal from './RecurringBookingModal'; // We need this
import { toast } from 'react-toastify';
import ConfirmDialog from './ConfirmDialog';
import DarkModeToggle from './DarkModeToggle';
import HjezlyLogo from './HjezlyLogo';

// --- A card just for customer bookings ---
const CustomerBookingCard = ({ booking, onCancelBooking, t }) => (
  <div className="group p-4 rounded-xl border-2 border-primary-light dark:border-primary/30 bg-primary/5 dark:bg-primary/10">
    <div className="flex flex-col sm:flex-row justify-between sm:items-center">
      {/* Left Side: Info */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-[140px]">
          <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
            <Clock size={20} className="text-white" />
          </div>
          <div>
            <div className="font-bold text-neutral-800 dark:text-white">{booking.startTime} - {booking.endTime}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <User size={16} className="text-neutral-600 dark:text-neutral-400" />
          <span className="font-semibold text-neutral-800 dark:text-white">{booking.customerName}</span>
        </div>
        <div className="flex items-center gap-2">
          <Phone size={16} className="text-neutral-600 dark:text-neutral-400" />
          <span className="text-neutral-700 dark:text-neutral-300">{booking.customerPhone}</span>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
          booking.paymentType === 'online' || booking.paymentType === 'partial-deposit'
            ? 'bg-primary/20 dark:bg-primary/30 text-primary-700 dark:text-primary-300'
            : 'bg-accent/20 dark:bg-accent/30 text-accent-700 dark:text-accent-300'
        }`}>
          {booking.paymentType === 'online' ? t('ownerdashboard.paymentOnline') : 
           booking.paymentType === 'partial-deposit' ? t('ownerdashboard.paymentDeposit') :
           t('ownerdashboard.paymentAtStadium')}
        </div>
      </div>
      {/* Right Side: Price & Delete */}
      <div className="flex items-center gap-3 mt-3 sm:mt-0">
        <div className="text-xl font-bold text-primary sm:text-right">${booking.price}</div>
        <button
          onClick={() => onCancelBooking(booking.id, booking.customerName, `${booking.startTime}-${booking.endTime}`)}
          className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-xl transition opacity-100 sm:opacity-0 group-hover:opacity-100"
          title="Cancel Booking"
        >
          <Trash2 size={18} />
        </button>
      </div>
    </div>
  </div>
);

// --- A card just for recurring blocks ---
const RecurringBlockCard = ({ block, t, onCancelBooking }) => (
  <div className="group p-4 rounded-xl border-2 border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20">
    <div className="flex flex-col sm:flex-row justify-between sm:items-center">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-[140px]">
          <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
            <CalendarDays size={20} className="text-white" />
          </div>
          <div>
            <div className="font-bold text-neutral-800 dark:text-white">{block.startTime} - {block.endTime}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-lg text-purple-800 dark:text-purple-400">{block.label}</span>
        </div>
        <div className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-semibold">
          {block.sportName}
        </div>
      </div>
      <div className="flex items-center gap-3 mt-3 sm:mt-0">
        <button
          onClick={() => onCancelBooking(block.id, block.label, `${block.startTime}-${block.endTime}`)}
          className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-xl transition opacity-100 sm:opacity-0 group-hover:opacity-100"
          title="Cancel This Instance"
        >
          <Trash2 size={18} />
        </button>
      </div>
    </div>
  </div>
);


// --- MAIN OWNER DASHBOARD ---
const OwnerDashboard = () => {
  const { t, i18n } = useTranslation();
  
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stadiumLoading, setStadiumLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showAddBooking, setShowAddBooking] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false); // Fix: Rename showSettings to showSettingsModal for consistency
  const [myStadium, setMyStadium] = useState(null);
  
  const [newBooking, setNewBooking] = useState({
    sportId: '', slotId: '', name: '', phone: '', players: '', paymentType: 'cash'
  });
  const [modalAvailableSlots, setModalAvailableSlots] = useState([]);
  const [modalState, setModalState] = useState('form');
  const [modalError, setModalError] = useState('');
  
  const [stadiumSettings, setStadiumSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false); // Fix: Add savingSettings state
  
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, bookingId: null, customerName: '', timeSlot: '' });

  // --- HELPER: Close Add Booking Modal ---
  const handleCloseModal = useCallback(() => {
    setShowAddBooking(false);
    setModalAvailableSlots([]);
    setNewBooking({
      sportId: '', slotId: '', name: '', phone: '', players: '', paymentType: 'cash'
    });
    setModalState('form');
    setModalError('');
  }, []);

  // --- HELPER: Close Settings Modal ---
  const handleSettingsClose = () => {
    setShowSettingsModal(false);
    setStadiumSettings(myStadium); // Reset changes
    setUploadError('');
  };

  const handleSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'coordinates.lat' || name === 'coordinates.lng') {
      const coordField = name.split('.')[1];
      setStadiumSettings(prev => ({
        ...prev,
        coordinates: {
          ...prev.coordinates,
          [coordField]: parseFloat(value) || 0
        }
      }));
      return;
    }
    
    // Handle nested policy fields like cancellationPolicy.windowHours
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setStadiumSettings(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setStadiumSettings(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const stadiumRef = doc(db, 'stadiums', myStadium.id);
      
      // Ensure we're saving proper numbers for policy fields
      const updatedSettings = {
        ...stadiumSettings,
        cancellationPolicy: {
          ...stadiumSettings.cancellationPolicy,
          windowHours: Number(stadiumSettings.cancellationPolicy?.windowHours || 24),
          refundPercentage: Number(stadiumSettings.cancellationPolicy?.refundPercentage || 0),
          lateRefundPercentage: Number(stadiumSettings.cancellationPolicy?.lateRefundPercentage || 0),
        },
        depositPercentage: Number(stadiumSettings.depositPercentage || 0)
      };

      await updateDoc(stadiumRef, updatedSettings);
      
      setMyStadium(updatedSettings); // Update local state
      toast.success(t('ownerdashboard.settingsSaved'), { position: "top-center" });
      setShowSettingsModal(false);
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error(t('ownerdashboard.settingsSavedError'), { position: "top-center" });
    }
    setSavingSettings(false);
  };

  useEffect(() => {
    findMyStadium();
  }, []);

  const loadBookings = useCallback(async () => {
    if (!myStadium) return;
    setLoading(true);
    try {
      const dateString = selectedDate.toISOString().split('T')[0];
      console.log('OwnerDashboard: Fetching bookings for stadiumId:', myStadium.id, 'on date:', dateString);
      const q = query(
        collection(db, 'bookings'),
        where('date', '==', dateString),
        where('stadiumId', '==', myStadium.id),
        orderBy('startTime', 'asc')
      );
      const querySnapshot = await getDocs(q);
      const bookingsData = [];
      querySnapshot.forEach((doc) => {
        const bookingData = { id: doc.id, ...doc.data() };
        // Filter out cancelled bookings
        if (bookingData.status !== 'cancelled') {
          bookingsData.push(bookingData);
        }
      });
      console.log('OwnerDashboard: Bookings fetched:', bookingsData);
      setBookings(bookingsData);
    } catch (error) {
      console.error('Error loading bookings:', error);
    }
    setLoading(false);
  }, [myStadium, selectedDate]);

  useEffect(() => {
    if (myStadium) {
      loadBookings();
    }
  }, [selectedDate, myStadium, loadBookings]);
  
  // Effect for "Enter" key on success modal
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Enter') {
        if (modalState === 'success') {
          handleCloseModal();
        }
      }
    };
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [modalState, handleCloseModal]);

  // Sync stadiumSettings with myStadium whenever settings modal opens
  useEffect(() => {
    if (showSettingsModal && myStadium) {
      setStadiumSettings(myStadium);
    }
  }, [showSettingsModal, myStadium]);


  const findMyStadium = async () => {
    setStadiumLoading(true);
    try {
      const userEmail = auth.currentUser?.email;
      console.log('[OwnerDashboard] findMyStadium called with email:', userEmail);
      
      if (!userEmail) {
        console.log('[OwnerDashboard] No userEmail, returning');
        setStadiumLoading(false);
        return;
      }
      
      // Don't try to find stadium for admin user
      if (userEmail === 'admin@bookstadium.com') {
        console.log('[OwnerDashboard] Admin user detected, skipping stadium lookup');
        setStadiumLoading(false);
        return;
      }
      
      console.log('[OwnerDashboard] Querying stadiums for owner:', userEmail);
      const stadiumsRef = collection(db, 'stadiums');
      const q = query(stadiumsRef, where('ownerEmail', '==', userEmail));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const stadiumDoc = querySnapshot.docs[0];
        console.log('[OwnerDashboard] Stadium found:', stadiumDoc.id);
        setMyStadium({ id: stadiumDoc.id, ...stadiumDoc.data() });
        setStadiumSettings(stadiumDoc.data());
      } else {
        console.error('[OwnerDashboard] No stadium found for this owner:', userEmail);
      }
    } catch (error) {
      console.error('[OwnerDashboard] Error finding stadium:', error);
    }
    setStadiumLoading(false);
  };

  // --- NEW SIMPLE loadBookings (No Index Error) ---
  const generateSlotsForSport = (sport) => {
    const slots = [];
    if (!sport || !sport.openTime || !sport.closeTime || !sport.duration) return slots;
    const durationMin = parseInt(sport.duration);
    const timeToMinutes = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };
    const minutesToTime = (totalMinutes) => {
      const hours = Math.floor(totalMinutes / 60).toString().padStart(2, '0');
      const minutes = (totalMinutes % 60).toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    };
    const openMin = timeToMinutes(sport.openTime);
    const closeMin = timeToMinutes(sport.closeTime);
    let currentMin = openMin;
    let slotIndex = 0;
    while (currentMin + durationMin <= closeMin) {
      const startTime = minutesToTime(currentMin);
      const endTime = minutesToTime(currentMin + durationMin);
      const slotId = `${sport.id}-slot-${slotIndex}`;
      slots.push({ id: slotId, startTime, endTime, price: sport.pricePerSlot });
      currentMin += durationMin;
      slotIndex++;
    }
    return slots;
  };

  // This function checks for conflicts when *manually* adding a booking
  const handleSportChange = (e) => {
    const sportId = e.target.value;
    setNewBooking({ ...newBooking, sportId: sportId, slotId: '' });
    if (sportId) {
      const selectedSport = myStadium.sports.find(s => s.id === sportId);
      const allSlots = generateSlotsForSport(selectedSport);
      
      // Get *all* already booked slots for this sport on this day
      const bookedSlotIds = new Set(
        bookings
          .filter(b => b.sportId === sportId)
          .map(b => b.slotId)
      );
            
      const availableSlots = allSlots.filter(slot => !bookedSlotIds.has(slot.id));
      
      setModalAvailableSlots(availableSlots);
    } else {
      setModalAvailableSlots([]);
    }
  };

  const handleLogout = async () => {
    try { await signOut(auth); } catch (error) { console.error('Logout error:', error); }
  };

  const handleAddBooking = async (event) => {
    event.preventDefault(); 
    if (!newBooking.sportId || !newBooking.slotId || !newBooking.name || !newBooking.phone || !newBooking.players) {
      setModalError(t('ownerdashboard.modalErrorFields'));
      return;
    }
    setModalError('');
    setModalState('submitting');
    try {
      const dateString = selectedDate.toISOString().split('T')[0];
      const selectedSport = myStadium.sports.find(s => s.id === newBooking.sportId);
      const allSlots = generateSlotsForSport(selectedSport);
      const selectedSlot = allSlots.find(s => s.id === newBooking.slotId);
      if (!selectedSport || !selectedSlot) {
        setModalError('Error: Could not find selected sport or slot.');
        setModalState('form');
        return;
      }
      await addDoc(collection(db, 'bookings'), {
        stadiumId: myStadium.id, stadiumName: myStadium.name,
        sportId: selectedSport.id, sportName: selectedSport.name,
        date: dateString, slotId: selectedSlot.id,
        startTime: selectedSlot.startTime, endTime: selectedSlot.endTime,
        customerName: newBooking.name, customerPhone: newBooking.phone,
        numPlayers: parseInt(newBooking.players), paymentType: newBooking.paymentType,
        price: selectedSport.pricePerSlot, createdAt: new Date().toISOString(),
        status: 'confirmed', createdBy: 'owner', 
        type: 'customer' // All manual bookings are "customer" type
      });
      setModalState('success');
      loadBookings(); // This will refresh the unified schedule
    } catch (error) {
      console.error('Error adding booking:', error);
      setModalError(t('ownerdashboard.modalErrorSubmit'));
      setModalState('form');
    }
  };

  const handleCancelBooking = async (bookingId, customerName, timeSlot) => {
    try {
      // First, fetch the booking to check if it's recurring
      const bookingRef = doc(db, 'bookings', bookingId);
      const bookingSnap = await getDoc(bookingRef);
      
      if (bookingSnap.exists()) {
        const bookingData = bookingSnap.data();
        
        // Check if it's a recurring booking (with or without group ID)
        if (bookingData.type === 'recurring') {
          setConfirmDialog({ 
            open: true, 
            bookingId, 
            customerName, 
            timeSlot,
            isRecurring: true,
            recurringGroupId: bookingData.recurringGroupId || null,
            label: bookingData.label || customerName
          });
        } else {
          // Regular customer booking
          setConfirmDialog({ 
            open: true, 
            bookingId, 
            customerName, 
            timeSlot,
            isRecurring: false
          });
        }
      } else {
        // Booking not found, just show regular dialog
        setConfirmDialog({ 
          open: true, 
          bookingId, 
          customerName, 
          timeSlot,
          isRecurring: false
        });
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
      // Fallback to regular dialog
      setConfirmDialog({ 
        open: true, 
        bookingId, 
        customerName, 
        timeSlot,
        isRecurring: false
      });
    }
  };

  const confirmCancelBooking = async (deleteAll = false) => {
    const dialog = confirmDialog;
    setConfirmDialog({ ...confirmDialog, open: false });
    
    try {
      if (deleteAll && dialog.isRecurring) {
        if (dialog.recurringGroupId) {
          // New recurring bookings with recurringGroupId
          const q = query(
            collection(db, 'bookings'),
            where('recurringGroupId', '==', dialog.recurringGroupId)
          );
          const querySnapshot = await getDocs(q);
          
          const batch = writeBatch(db);
          querySnapshot.forEach((doc) => {
            batch.delete(doc.ref);
          });
          
          await batch.commit();
          toast.success(t('ownerdashboard.allInstancesDeleted') || `All instances of "${dialog.label}" deleted successfully`);
        } else {
          // Old recurring bookings without recurringGroupId - search by label, startTime, endTime
          const bookingSnap = await getDoc(doc(db, 'bookings', dialog.bookingId));
          if (bookingSnap.exists()) {
            const bookingData = bookingSnap.data();
            
            const q = query(
              collection(db, 'bookings'),
              where('stadiumId', '==', bookingData.stadiumId),
              where('sportId', '==', bookingData.sportId),
              where('type', '==', 'recurring'),
              where('label', '==', bookingData.label),
              where('startTime', '==', bookingData.startTime),
              where('endTime', '==', bookingData.endTime)
            );
            const querySnapshot = await getDocs(q);
            
            const batch = writeBatch(db);
            querySnapshot.forEach((doc) => {
              batch.delete(doc.ref);
            });
            
            await batch.commit();
            toast.success(t('ownerdashboard.allInstancesDeleted') || `All instances of "${bookingData.label}" deleted successfully`);
          }
        }
      } else {
        // Delete only this instance
        await deleteDoc(doc(db, 'bookings', dialog.bookingId));
        toast.success(t('ownerdashboard.bookingCancelled'));
      }
      
      loadBookings();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error(t('ownerdashboard.errorCancelling') || 'Error cancelling booking');
    }
  };

  const cancelDialog = () => {
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  const handleRecurringConfirm = async () => {
    // Callback after recurring booking is saved
    setShowRecurringModal(false);
    loadBookings(); // Refresh bookings to show the new recurring blocks
  };

  const handleCloseSettingsModal = () => {
    setShowSettingsModal(false);
    setSavingSettings('idle');
    setUploadError('');
    setStadiumSettings(myStadium); 
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    
    // Validate required fields
    if (!stadiumSettings.name || !stadiumSettings.location || !stadiumSettings.phone) {
      toast.error(t('ownerdashboard.modalSettingsErrorFields'));
      setSavingSettings(false);
      return;
    }
    
    try {
      const updatedStadium = {
        ...stadiumSettings,
        ownerEmail: auth.currentUser.email,
        updatedAt: new Date().toISOString()
      };
      
      // Update stadium document
      const docRef = doc(db, 'stadiums', myStadium.id);
      await setDoc(docRef, updatedStadium, { merge: true });
      
      // Update local state
      setMyStadium(updatedStadium);
      setStadiumSettings(updatedStadium);
      
      toast.success(t('ownerdashboard.modalSettingsSaved'));
      setShowSettingsModal(false);
    } catch (error) {
      console.error("Error updating stadium:", error);
      toast.error(t('ownerdashboard.errorSubmit'));
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSportUpdate = (index, field, value) => {
    const updatedSports = [...stadiumSettings.sports];
    const sport = { ...updatedSports[index] };
    sport[field] = value;
    if (field === 'pricePerSlot' || field === 'capacity' || field === 'duration') {
      sport[field] = parseInt(value) || 0;
    }
    updatedSports[index] = sport;
    setStadiumSettings({ ...stadiumSettings, sports: updatedSports });
  };

  const handleAddSport = () => {
    const newSport = {
      id: `sport-${Date.now()}`, name: 'New Sport', type: 'football',
      capacity: 10, pricePerSlot: 30, duration: 90,
      openTime: '08:00', closeTime: '23:00'
    };
    setStadiumSettings({
      ...stadiumSettings,
      sports: [...stadiumSettings.sports, newSport]
    });
  };

  const handleRemoveSport = (index) => {
    if (window.confirm('Are you sure you want to remove this sport?')) {
      const updatedSports = stadiumSettings.sports.filter((_, i) => i !== index);
      setStadiumSettings({ ...stadiumSettings, sports: updatedSports });
    }
  };

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const handlePhotoUpload = async (event) => {
    // This requires Blaze plan
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setUploadError(t('ownerdashboard.modalSettingsUploadError'));
      return;
    }
    setUploading(true);
    setUploadError('');
    try {
      const filePath = `stadiums/${myStadium.id}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, filePath);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      setStadiumSettings(prev => ({
        ...prev,
        photos: [...(prev.photos || []), downloadURL]
      }));
    } catch (error) {
      console.error("Error uploading photo:", error);
      setUploadError(t('ownerdashboard.modalSettingsUploadError2'));
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoDelete = async (photoURL, index) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    try {
      const newPhotos = stadiumSettings.photos.filter((_, i) => i !== index);
      setStadiumSettings(prev => ({ ...prev, photos: newPhotos }));
      const photoRef = ref(storage, photoURL);
      await deleteObject(photoRef);
      const docRef = doc(db, 'stadiums', myStadium.id);
      await updateDoc(docRef, { photos: newPhotos });
      setMyStadium(prev => ({...prev, photos: newPhotos}));
    } catch (error) {
      console.error("Error deleting photo:", error);
      toast.error(t('ownerdashboard.deletePhotoFailed'));
      setStadiumSettings(myStadium);
    }
  };

  const formatDate = (date) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString(i18n.language === 'ar' ? 'ar-LB' : 'en-US', options);
  };
  
  const changeDate = (days) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + days);
    setSelectedDate(newDate);
  };

  const isToday = selectedDate.toDateString() === new Date().toDateString();
  
  // Stats are now based on customer bookings only
  const customerBookings = bookings.filter(b => b.type === 'customer' || !b.type);
  const todayRevenue = customerBookings.reduce((sum, b) => sum + (b.price || 0), 0);
  const todayPlayers = customerBookings.reduce((sum, b) => sum + (b.numPlayers || 0), 0);

  if (stadiumLoading) {
    return (
      <div className="min-h-screen bg-neutral-100 dark:bg-neutral-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
          <div className="text-xl text-neutral-600 dark:text-neutral-400">Loading your stadium...</div>
        </div>
      </div>
    );
  }

  if (!myStadium || !stadiumSettings) {
    return (
      <div className="min-h-screen bg-neutral-100 dark:bg-neutral-900 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle size={64} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-neutral-800 dark:text-white mb-2">No Stadium Found</h2>
          <p className="text-neutral-600 dark:text-neutral-400 mb-6">
            Your account is not linked to any stadium. Please contact Hjezly support.
          </p>
          <button
            onClick={handleLogout}
            className="bg-primary text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-dark"
          >
            Logout
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-neutral-100 dark:bg-neutral-900 ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      
      {/* Header */}
      <div className="bg-primary text-white shadow-2xl">
        <div className="max-w-7xl mx-auto p-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <div className="text-sm opacity-90 mb-2 font-medium">{t('ownerdashboard.headerTitle')}</div>
              <div className="flex justify-between items-center mb-6 bg-white dark:bg-neutral-800 p-4 rounded-xl shadow-sm border border-neutral-200 dark:border-neutral-700">
                <div className="flex items-center gap-3">
                  <HjezlyLogo size="sm" variant="icon" />
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-700 via-primary-500 to-primary-700 bg-clip-text text-transparent">
                    {myStadium ? myStadium.name[i18n.language] : t('ownerdashboard.title')}
                  </h1>
                </div>
                <div className="flex items-center gap-2">
                  <DarkModeToggle />
                  <button 
                    onClick={() => i18n.changeLanguage(i18n.language === 'en' ? 'ar' : 'en')}
                    className="flex items-center justify-center gap-2 bg-white/10 backdrop-blur-md hover:bg-white/20 text-white px-5 py-3 rounded-xl font-semibold transition border border-white/20"
                  >
                    <Globe size={18} />
                    {i18n.language === 'en' ? 'العربية' : 'English'}
                  </button>
                  <button 
                    onClick={handleLogout}
                    className="flex items-center justify-center gap-2 bg-white text-primary px-5 py-3 rounded-xl font-semibold hover:bg-neutral-100 transition shadow-lg"
                  >
                    <LogOut size={18} />
                    {t('ownerdashboard.logout')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white dark:bg-neutral-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition transform hover:-translate-y-1 border-l-4 border-primary">
            <div className="flex justify-between items-start mb-4">
              <div className="text-neutral-600 dark:text-neutral-400 text-sm font-semibold">
                {isToday ? t('ownerdashboard.bookingsToday') : t('ownerdashboard.bookingsSelected')}
              </div>
              <Calendar size={24} className="text-primary" />
            </div>
            <div className="text-4xl font-bold text-neutral-800 dark:text-white mb-2">{customerBookings.length}</div>
            <div className="text-sm text-primary font-medium">
              {customerBookings.length === 0 ? t('ownerdashboard.noBookings') : `${customerBookings.length} ${t('ownerdashboard.confirmed')}`}
            </div>
          </div>
          <div className="bg-white dark:bg-neutral-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition transform hover:-translate-y-1 border-l-4 border-blue-500">
            <div className="flex justify-between items-start mb-4">
              <div className="text-neutral-600 dark:text-neutral-400 text-sm font-semibold">{t('ownerdashboard.revenue')}</div>
              <DollarSign size={24} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div className="text-4xl font-bold text-neutral-800 dark:text-white mb-2">${todayRevenue}</div>
            <div className="text-sm text-blue-600 dark:text-blue-400 font-medium">
              {isToday ? t('ownerdashboard.today') : t('ownerdashboard.selectedDate')}
            </div>
          </div>
          <div className="bg-white dark:bg-neutral-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition transform hover:-translate-y-1 border-l-4 border-purple-500">
            <div className="flex justify-between items-start mb-4">
              <div className="text-neutral-600 dark:text-neutral-400 text-sm font-semibold">{t('ownerdashboard.totalPlayers')}</div>
              <Users size={24} className="text-purple-600 dark:text-purple-400" />
            </div>
            <div className="text-4xl font-bold text-neutral-800 dark:text-white mb-2">{todayPlayers}</div>
            <div className="text-sm text-purple-600 dark:text-purple-400 font-medium">
              {t('ownerdashboard.playersSubtext')}
            </div>
          </div>
        </div>

        {/* Date Navigation Card */}
        <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center justify-between gap-2 md:gap-4 w-full md:w-auto">
              <button 
                onClick={() => changeDate(-1)}
                className="w-10 h-10 md:w-12 md:h-12 flex-shrink-0 flex items-center justify-center bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded-xl transition"
              >
                <ChevronLeft size={24} className="dark:text-white" />
              </button>
              <div className="flex-1 text-center bg-primary/10 dark:bg-primary/20 px-4 md:px-8 py-4 rounded-xl border-2 border-primary-light dark:border-primary/30">
                <div className="text-xl md:text-2xl font-bold text-neutral-800 dark:text-white">
                  {formatDate(selectedDate)}
                </div>
                {isToday && (
                  <div className="text-sm text-primary font-semibold mt-1">{t('ownerdashboard.today')}</div>
                )}
              </div>
              <button 
                onClick={() => changeDate(1)}
                className="w-10 h-10 md:w-12 md:h-12 flex-shrink-0 flex items-center justify-center bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded-xl transition"
              >
                <ChevronRight size={24} className="dark:text-white" />
              </button>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              <button
                onClick={() => setShowRecurringModal(true)}
                className="flex items-center justify-center gap-2 bg-purple-600 text-white px-6 py-4 rounded-xl font-semibold hover:bg-purple-700 transition shadow-lg hover:shadow-xl w-full"
              >
                <CalendarDays size={20} />
                {t('recurringmodal.addButton')}
              </button>
              <button
                onClick={() => setShowAddBooking(true)}
                className="flex items-center justify-center gap-2 bg-primary text-white px-6 py-4 rounded-xl font-semibold hover:bg-primary-dark transition shadow-lg hover:shadow-xl w-full"
              >
                <Plus size={20} />
                {t('ownerdashboard.addBooking')}
              </button>
            </div>
          </div>
        </div>

        {/* --- NEW, GROUPED BOOKINGS LIST (BY SPORT/FIELD) --- */}
        <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-lg p-6">
          <h2 className="text-2xl font-bold text-neutral-800 dark:text-white mb-6 flex items-center gap-3">
            <Calendar className="text-primary" size={28} />
            {t('ownerdashboard.scheduleTitle')}
          </h2>
          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4"></div>
              <div className="text-neutral-600 dark:text-neutral-400">{t('ownerdashboard.loading')}</div>
            </div>
          ) : (!myStadium?.sports || myStadium.sports.length === 0) ? (
            <div className="text-center py-16">
              <AlertCircle size={64} className="mx-auto text-neutral-300 dark:text-neutral-600 mb-4" />
              <p className="text-neutral-500 dark:text-neutral-400 text-xl font-semibold">{t('ownerdashboard.noSports')}</p>
            </div>
          ) : (
            <div className="space-y-8">
              {myStadium.sports.map((sport) => {
                // Filter bookings for this sport
                const sportBookings = bookings.filter(b => b.sportId === sport.id);
                return (
                  <div key={sport.id} className="border-b pb-6 mb-6">
                    <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
                      {sport.name} <span className="text-sm text-neutral-500">({sport.type})</span>
                    </h3>
                    {sportBookings.length === 0 ? (
                      <div className="text-neutral-400 text-sm mb-4">{t('ownerdashboard.noBookingsDate')}</div>
                    ) : (
                      <div className="space-y-3">
                        {sportBookings.map((booking) => {
                          if (booking.type === 'recurring') {
                            return <RecurringBlockCard key={booking.id} block={booking} t={t} onCancelBooking={handleCancelBooking} />;
                          }
                          return (
                            <CustomerBookingCard 
                              key={booking.id} 
                              booking={booking} 
                              onCancelBooking={handleCancelBooking} 
                              t={t} 
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {/* --- END REWORKED LIST --- */}
      </div>

      {/* Add Booking Modal */}
      {showAddBooking && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 z-[60] animate-fade-in overflow-y-auto">
          <div className="bg-white/95 dark:bg-neutral-900/95 backdrop-blur-xl rounded-3xl border border-white/20 dark:border-neutral-700/50 max-w-md w-full p-8 relative my-8 shadow-[0_8px_32px_0_rgba(31,38,135,0.37)]">
            {modalState === 'form' && (
              <form onSubmit={handleAddBooking} className="space-y-4">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-neutral-800 dark:text-white">{t('ownerdashboard.modalAddTitle')}</h2>
                  <button 
                    type="button"
                    onClick={handleCloseModal}
                    className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-full transition"
                  >
                    <X size={24} className="dark:text-white" />
                  </button>
                </div>
                {modalError && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-4 text-center">
                    {modalError}
                  </div>
                )}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">{t('ownerdashboard.modalSportLabel')}</label>
                    <select
                      value={newBooking.sportId}
                      onChange={handleSportChange}
                      className="w-full px-4 py-3 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      <option value="">{t('ownerdashboard.modalSportPlaceholder')}</option>
                      {myStadium?.sports?.map(sport => (
                        <option key={sport.id} value={sport.id}>
                          {sport.name} (${sport.pricePerSlot})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">{t('ownerdashboard.modalSlotLabel')}</label>
                    <select
                      value={newBooking.slotId}
                      onChange={(e) => setNewBooking({...newBooking, slotId: e.target.value})}
                      disabled={!newBooking.sportId}
                      className="w-full px-4 py-3 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-neutral-100 dark:disabled:bg-neutral-800 disabled:cursor-not-allowed"
                    >
                      <option value="">{t('ownerdashboard.modalSlotPlaceholder')}</option>
                      {modalAvailableSlots.length > 0 && modalAvailableSlots.map(slot => (
                        <option key={slot.id} value={slot.id}>
                          {slot.startTime} - {slot.endTime}
                        </option>
                      ))}
                      {modalAvailableSlots.length === 0 && newBooking.sportId && (
                        <option value="" disabled>{t('ownerdashboard.modalSlotNone')}</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">{t('ownerdashboard.modalNameLabel')}</label>
                    <input
                      type="text"
                      value={newBooking.name}
                      onChange={(e) => setNewBooking({...newBooking, name: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder={t('ownerdashboard.modalNamePlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">{t('ownerdashboard.modalPhoneLabel')}</label>
                    <input
                      type="tel"
                      value={newBooking.phone}
                      onChange={(e) => setNewBooking({...newBooking, phone: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="03 123 456"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">{t('ownerdashboard.modalPlayersLabel')}</label>
                    <input
                      type="number"
                      min="1"
                      value={newBooking.players}
                      onChange={(e) => setNewBooking({...newBooking, players: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="10"
                    />
                  </div>
                  
                  <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <Check size={20} className="text-blue-600 dark:text-blue-300" />
                      <div>
                        <p className="font-semibold text-blue-900 dark:text-blue-200">
                          {t('ownerdashboard.modalPaymentNote')}
                        </p>
                        <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                          {t('ownerdashboard.modalPayAtStadium')}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-3 pt-4 flex-col sm:flex-row">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="flex-1 px-6 py-4 border-2 border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 rounded-xl font-semibold hover:bg-neutral-50 dark:hover:bg-neutral-700 transition text-base"
                    >
                      {t('ownerdashboard.modalCancel')}
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-6 py-4 bg-primary text-white rounded-xl font-semibold hover:bg-primary-dark transition text-base"
                    >
                      {t('ownerdashboard.modalAddButton')}
                    </button>
                  </div>
                </div>
              </form>
            )}
            {modalState === 'submitting' && (
              <div className="text-center py-16">
                <div className="inline-block w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-6"></div>
                <h2 className="text-2xl font-bold text-neutral-800 dark:text-white">Adding Booking...</h2>
              </div>
            )}
            {modalState === 'success' && (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-primary/20 dark:bg-primary/30 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check size={48} className="text-primary" />
                </div>
                <h2 className="text-3xl font-bold text-neutral-800 dark:text-white mb-3">Booking Added!</h2>
                <p className="text-lg text-neutral-600 dark:text-neutral-400 mb-8">
                  The new booking has been added to your schedule.
                </p>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="w-full px-6 py-4 bg-primary text-white rounded-xl font-semibold hover:bg-primary-dark transition"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 z-[60] animate-fade-in overflow-y-auto">
          <div className="bg-white/95 dark:bg-neutral-900/95 backdrop-blur-xl rounded-3xl border border-white/20 dark:border-neutral-700/50 max-w-4xl w-full p-8 my-8 shadow-[0_8px_32px_0_rgba(31,38,135,0.37)]">
            <form onSubmit={handleSettingsSubmit}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-neutral-800 dark:text-white">{t('ownerdashboard.modalSettingsTitle')}</h2>
                <button 
                  type="button"
                  onClick={handleCloseSettingsModal}
                  className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-full"
                >
                  <X size={24} className="dark:text-white" />
                </button>
              </div>
              <div className="max-h-[70vh] overflow-y-auto pr-2 space-y-8">
                <div>
                  <h3 className="text-xl font-semibold text-neutral-800 dark:text-white mb-4 border-b dark:border-neutral-700 pb-2">{t('ownerdashboard.modalSettingsBasic')}</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">{t('ownerdashboard.modalSettingsName')}</label>
                      <input
                        type="text"
                        value={stadiumSettings.name}
                        onChange={(e) => setStadiumSettings({...stadiumSettings, name: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-xl focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">{t('ownerdashboard.modalSettingsLocation')}</label>
                      <input
                        type="text"
                        value={stadiumSettings.location}
                        onChange={(e) => setStadiumSettings({...stadiumSettings, location: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-xl focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    {/* New: Advanced Location Settings (Coordinates) */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">{t('ownerdashboard.modalSettingsLat')}</label>
                        <input
                          type="number"
                          step="any"
                          name="coordinates.lat"
                          value={stadiumSettings.coordinates?.lat || ''}
                          onChange={handleSettingsChange} 
                          className="w-full px-4 py-3 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-xl focus:ring-2 focus:ring-primary"
                          placeholder="Latitude (e.g. 33.89)"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">{t('ownerdashboard.modalSettingsLng')}</label>
                        <input
                          type="number"
                          step="any"
                          name="coordinates.lng"
                          value={stadiumSettings.coordinates?.lng || ''}
                          onChange={handleSettingsChange}
                          className="w-full px-4 py-3 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-xl focus:ring-2 focus:ring-primary"
                          placeholder="Longitude (e.g. 35.50)"
                        />
                      </div>
                      <div className="col-span-2 text-xs text-neutral-500 dark:text-neutral-400">
                         {t('ownerdashboard.modalSettingsCoordHelp')} <a href="https://www.google.com/maps" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Maps</a>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">{t('ownerdashboard.modalSettingsPhone')}</label>
                      <input
                        type="tel"
                        value={stadiumSettings.phone}
                        onChange={(e) => setStadiumSettings({...stadiumSettings, phone: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-xl focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-center mb-4 border-b dark:border-neutral-700 pb-2">
                    <h3 className="text-xl font-semibold text-neutral-800 dark:text-white">{t('ownerdashboard.modalSettingsSportsTitle')}</h3>
                    <button
                      type="button"
                      onClick={handleAddSport}
                      className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg font-semibold hover:bg-primary-dark transition"
                    >
                      <Plus size={18} />
                      {t('ownerdashboard.modalSettingsAddSport')}
                    </button>
                  </div>
                  <div className="space-y-4">
                    {stadiumSettings.sports.map((sport, index) => (
                      <div key={index} className="p-4 border border-neutral-200 dark:border-neutral-700 rounded-xl bg-neutral-50 dark:bg-neutral-800 space-y-4">
                        <div className="flex justify-between items-center">
                          <input
                            type="text"
                            value={sport.name}
                            onChange={(e) => handleSportUpdate(index, 'name', e.target.value)}
                            className="text-lg font-semibold text-neutral-800 dark:text-white dark:bg-neutral-900 w-full p-2 border-b border-transparent focus:border-primary focus:bg-white dark:focus:bg-neutral-800"
                            placeholder={t('ownerdashboard.modalSettingsSportName')}
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveSport(index)}
                            className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-400 mb-1">{t('ownerdashboard.modalSettingsSportType')}</label>
                            <select
                              value={sport.type}
                              onChange={(e) => handleSportUpdate(index, 'type', e.target.value)}
                              className="w-full px-3 py-2 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary"
                            >
                              <option value="football">Football</option>
                              <option value="padel">Padel</option>
                              <option value="basketball">Basketball</option>
                              <option value="tennis">Tennis</option>
                              <option value="volleyball">Volleyball</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-400 mb-1">{t('ownerdashboard.modalSettingsCapacity')}</label>
                            <input
                              type="number"
                              value={sport.capacity}
                              onChange={(e) => handleSportUpdate(index, 'capacity', e.target.value)}
                              className="w-full px-3 py-2 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-400 mb-1">{t('ownerdashboard.modalSettingsPrice')}</label>
                            <input
                              type="number"
                              value={sport.pricePerSlot}
                              onChange={(e) => handleSportUpdate(index, 'pricePerSlot', e.target.value)}
                              className="w-full px-3 py-2 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-400 mb-1">{t('ownerdashboard.modalSettingsDuration')}</label>
                            <input
                              type="number"
                              value={sport.duration}
                              onChange={(e) => handleSportUpdate(index, 'duration', e.target.value)}
                              className="w-full px-3 py-2 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary"
                              placeholder="e.g., 60 or 90"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-400 mb-1">{t('ownerdashboard.modalSettingsOpen')}</label>
                            <input
                              type="time"
                              value={sport.openTime}
                              onChange={(e) => handleSportUpdate(index, 'openTime', e.target.value)}
                              className="w-full px-3 py-2 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-400 mb-1">{t('ownerdashboard.modalSettingsClose')}</label>
                            <input
                              type="time"
                              value={sport.closeTime}
                              onChange={(e) => handleSportUpdate(index, 'closeTime', e.target.value)}
                              className="w-full px-3 py-2 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Cancellation Policy Section */}
                <div>
                  <h3 className="text-xl font-semibold text-neutral-800 dark:text-white mb-4 border-b dark:border-neutral-700 pb-2">
                    {t('ownerdashboard.cancellationPolicyTitle')}
                  </h3>
                  <div className="bg-primary/5 dark:bg-primary/10 border border-primary/20 dark:border-primary/30 rounded-xl p-5 space-y-4">
                    
                    {/* NEW: Online Payment Toggle */}
                    <div className="bg-white dark:bg-neutral-800 p-4 rounded-xl border border-neutral-200 dark:border-neutral-700 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <DollarSign size={20} className="text-primary" />
                          <h4 className="font-semibold text-neutral-800 dark:text-white">
                            {t('ownerdashboard.onlinePaymentTitle')}
                          </h4>
                        </div>
                        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                          {t('ownerdashboard.onlinePaymentDesc')}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          className="sr-only peer"
                          checked={stadiumSettings.onlinePaymentRequired || false}
                          onChange={(e) => {
                            const isRequired = e.target.checked;
                            setStadiumSettings(prev => ({
                              ...prev,
                              onlinePaymentRequired: isRequired,
                              // Update policy type based on toggle
                              cancellationPolicy: {
                                ...prev.cancellationPolicy,
                                type: isRequired 
                                  ? (prev.cancellationPolicy?.type === 'pay-at-venue' ? 'full-payment' : prev.cancellationPolicy?.type) 
                                  : 'pay-at-venue',
                                requiresPayment: isRequired
                              }
                            }));
                          }}
                        />
                        <div className="w-14 h-7 bg-neutral-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/30 rounded-full peer dark:bg-neutral-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>
                    
                    {/* Policy Type - Show when online payment is ON */}
                    {stadiumSettings.onlinePaymentRequired && (
                        <div>
                        <label className="block text-sm font-semibold text-neutral-700 dark:text-neutral-300 mb-2">
                            {t('ownerdashboard.policyType')}
                        </label>
                        <select
                            value={stadiumSettings.cancellationPolicy?.type === 'pay-at-venue' ? 'full-payment' : stadiumSettings.cancellationPolicy?.type || 'full-payment'}
                            onChange={(e) => {
                            const newValue = e.target.value;
                            setStadiumSettings({
                                ...stadiumSettings,
                                cancellationPolicy: {
                                ...stadiumSettings.cancellationPolicy,
                                type: newValue,
                                requiresPayment: true,
                                depositPercentage: newValue === 'partial-deposit' ? 30 : (newValue === 'full-payment' ? 100 : 0)
                                }
                            });
                            }}
                            className="w-full px-4 py-3 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-xl focus:ring-2 focus:ring-primary"
                        >
                            <option value="partial-deposit">{t('ownerdashboard.policyPartialDeposit')}</option>
                            <option value="full-payment">{t('ownerdashboard.policyFullPayment')}</option>
                        </select>
                        <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1">
                            {stadiumSettings.cancellationPolicy?.type === 'full-payment'
                            ? t('ownerdashboard.policyFullPaymentDesc')
                            : t('ownerdashboard.policyPartialDepositDesc')}
                        </p>
                        </div>
                    )}
                    
                    {/* Policy Details - Cancellation Window, Refund % - Always show settings if relevant */}
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                        <div>
                          <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-400 mb-1">
                            {t('ownerdashboard.policyCancellationWindow')} ({t('ownerdashboard.policyHours')})
                          </label>
                          <input
                            type="number"
                             min="0"
                            value={stadiumSettings.cancellationPolicy?.cancellationWindow || 24}
                            onChange={(e) => setStadiumSettings({
                              ...stadiumSettings,
                              cancellationPolicy: {
                                ...stadiumSettings.cancellationPolicy,
                                cancellationWindow: parseInt(e.target.value) || 0
                              }
                            })}
                            className="w-full px-3 py-2 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        
                         {/* Show Refund % only if online payment is ON (or policy implies payment) */}
                        {stadiumSettings.onlinePaymentRequired && (
                            <div>
                            <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-400 mb-1">
                                {t('ownerdashboard.policyRefundPercentage')} (%)
                            </label>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={stadiumSettings.cancellationPolicy?.refundPercentage !== undefined ? stadiumSettings.cancellationPolicy.refundPercentage : 100}
                                onChange={(e) => setStadiumSettings({
                                ...stadiumSettings,
                                cancellationPolicy: {
                                    ...stadiumSettings.cancellationPolicy,
                                    refundPercentage: parseInt(e.target.value) || 0
                                }
                                })}
                                className="w-full px-3 py-2 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary"
                            />
                            </div>
                        )}

                        {/* Late Refund Percentage - NEW */}
                        {stadiumSettings.onlinePaymentRequired && (
                            <div>
                            <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-400 mb-1">
                                {t('ownerdashboard.policyLateRefundPercentage')} (%)
                            </label>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={stadiumSettings.cancellationPolicy?.lateRefundPercentage !== undefined ? stadiumSettings.cancellationPolicy.lateRefundPercentage : 0}
                                onChange={(e) => setStadiumSettings({
                                ...stadiumSettings,
                                cancellationPolicy: {
                                    ...stadiumSettings.cancellationPolicy,
                                    lateRefundPercentage: parseInt(e.target.value) || 0
                                }
                                })}
                                className="w-full px-3 py-2 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary"
                            />
                            <p className="text-[10px] text-neutral-500 mt-1">{t('ownerdashboard.policyLateRefundPercentageDesc')}</p>
                            </div>
                        )}

                        {/* If partial deposit, show deposit percentage */}
                         {stadiumSettings.onlinePaymentRequired && stadiumSettings.cancellationPolicy?.type === 'partial-deposit' && (
                            <div>
                            <label className="block text-xs font-semibold text-neutral-600 dark:text-neutral-400 mb-1">
                                {t('ownerdashboard.policyDepositPercentage')} (%)
                            </label>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={stadiumSettings.cancellationPolicy?.depositPercentage || 30}
                                onChange={(e) => setStadiumSettings({
                                ...stadiumSettings,
                                cancellationPolicy: {
                                    ...stadiumSettings.cancellationPolicy,
                                    depositPercentage: parseInt(e.target.value) || 0
                                }
                                })}
                                className="w-full px-3 py-2 border-2 border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary"
                            />
                            </div>
                        )}
                      </div>
                  </div>
                </div>
              </div>
              
              {/* Modal Actions */}
              <div className="p-6 border-t border-neutral-200 dark:border-neutral-700 flex justify-end gap-3 sticky bottom-0 bg-white dark:bg-neutral-800 rounded-b-2xl z-10">
                <button
                  type="button"
                  onClick={() => setShowSettingsModal(false)}
                  className="px-4 py-2 text-sm font-medium text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                >
                  {t('ownerdashboard.modalCancel')}
                </button>
                <button
                  type="submit"
                  disabled={uploading || savingSettings}
                  className="px-6 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {savingSettings ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin ltr:mr-2 rtl:ml-2"></div>
                      {t('ownerdashboard.modalSettingsSaving')}
                    </>
                  ) : (
                    t('ownerdashboard.modalSettingsSave')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- RECURRING BOOKING MODAL --- */}
      {showRecurringModal && (
        <RecurringBookingModal
          stadium={myStadium}
          onClose={() => setShowRecurringModal(false)}
          onSave={handleRecurringConfirm}
        />
      )}

      {confirmDialog.open && (
        <ConfirmDialog 
          open={confirmDialog.open}
          message={confirmDialog.isRecurring 
            ? t('ownerdashboard.confirmCancelRecurring', { label: confirmDialog.label, timeSlot: confirmDialog.timeSlot })
            : t('ownerdashboard.confirmCancel', { name: confirmDialog.customerName, slot: confirmDialog.timeSlot })
          }
          onConfirm={() => confirmCancelBooking(false)}
          onCancel={cancelDialog}
          isRecurring={confirmDialog.isRecurring}
          onConfirmAll={confirmDialog.isRecurring ? () => confirmCancelBooking(true) : null}
          confirmText={t('common.confirm')}
          cancelText={t('common.cancel')}
          rtl={i18n.language === 'ar'}
        />
      )}
    </div>
  );
};

export default OwnerDashboard;